import { useQueryClient } from "@tanstack/react-query";
import { Button, Switch, Table } from "antd";
import { type HTMLAttributes, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";
import PermissionCheck from "~Common/PermissionCheck";
import { ExportModal } from "~components/ExportModal/ExportModal.tsx";
import { ImportModal } from "~components/ImportButton/ImportModal.tsx";
import { TableColumnSettings } from "~components/TableColumnSettings/TableColumnSettings.tsx";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { RoleStatuses } from "~enums/roleStatuses.ts";
import { Roles } from "~enums/roles";
import { useAddRole } from "~hooks/apiHooks/useAddRole";
import { useDeleteRole } from "~hooks/apiHooks/useDeleteRole";
import { useNotification } from "~hooks/useNotification";
import { useTableColumns } from "~hooks/useTableColumns.ts";
import AddPlayerToFixtureRoster from "~pages/Roster/components/AddPlayerToRoster/AddPlayerToFixtureRoster";
import AddPlayerToRosterModal from "~pages/Roster/components/AddPlayerToRoster/AddPlayerToRosterModal";
import AddPlayerToSeasonRoster from "~pages/Roster/components/AddPlayerToRoster/AddPlayerToSeasonRoster";
import {
  defaultSeasonRosterExportFieldNames,
  seasonRosterExportFields,
} from "~pages/Roster/components/RosterTable/constants/exportFields.ts";
import { useExportSeasonRoster } from "~pages/Roster/components/RosterTable/hooks/useExportSeasonRoster.ts";
import { useGetRosterColumns } from "~pages/Roster/components/RosterTable/hooks/useGetRosterColumns";
import { useImportSeasonRoster } from "~pages/Roster/components/RosterTable/hooks/useImportSeasonRoster.tsx";
import type { RosterTableRow } from "~pages/Roster/components/RosterTable/types/rosterTableRow";
import type { TableColumnGroupDefinition } from "~types/tableColumnGroupDefinition";
import classNames from "~utils/classNames";
import styles from "./RosterTable.module.css";

type TableCellProps = HTMLAttributes<HTMLTableCellElement> & {
  isEditing: boolean;
  record: any;
};

const TableCell = ({ isEditing, record, ...props }: TableCellProps) => (
  <td {...props} className={classNames(props.className, isEditing ? styles.editCell : styles.cell)} />
);

type Props = {
  data: RosterTableRow[];
  isLoading: boolean;
  pagination: {
    currentPage: number;
    pageSize: number;
    pageSizeOptions: Array<number>;
    offset: number;
    handlePaginationChange: (current: number, size: number) => void;
  };
  handleUpsert: (value: any) => Promise<void>;
  handleDelete: (id: string) => Promise<void>;
  queryKey: string;
  entityId?: string;
  maxRosterSize?: number | null;
  maxStarterSize?: number | null;
  isRosterEditable: boolean;
  columnSettingsLocalStorageKey: string;
  columnSortLocalStorageKey: string;
  tableConfiguration: Array<TableColumnGroupDefinition>;
};

const RosterTable = ({
  isLoading,
  data,
  pagination,
  handleUpsert,
  handleDelete,
  queryKey,
  entityId,
  maxRosterSize,
  maxStarterSize,
  isRosterEditable,
  columnSettingsLocalStorageKey,
  columnSortLocalStorageKey,
  tableConfiguration,
}: Props) => {
  const [massEditMode, setMassEditMode] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);

  const queryClient = useQueryClient();
  const { sport, organizationId, seasonId, fixtureId, customerId } = useParams();
  const navigate = useNavigate();
  const notification = useNotification();
  const { formatMessage } = useIntl();
  const { mutateAsync: startImporting } = useImportSeasonRoster(entityId || "");
  const { mutateAsync: startExport, isPending: isExportPending } = useExportSeasonRoster(entityId || "");

  const refetch = async () => {
    setIsRefetching(true);
    await queryClient.invalidateQueries({
      queryKey: [queryKey, sport, organizationId, seasonId, entityId, fixtureId],
    });
    setIsRefetching(false);
  };

  const onMassEditModeChangedHandler = () => {
    setMassEditMode((prevChecked) => {
      if (prevChecked) {
        refetch();
      }
      return !prevChecked;
    });
  };

  const { mutateAsync: deleteCaptainRole, isPending: isDeletingCaptain } = useDeleteRole({
    onSuccess: async () => {
      refetch();
      notification.success({
        message: formatMessage({ id: "roster.update.success" }),
        placement: "bottomRight",
      });
    },
  });
  const { mutateAsync: createCaptainRole, isPending: isCreatingCaptain } = useAddRole({
    onSuccess: async () => {
      refetch();
      notification.success({
        message: formatMessage({ id: "roster.update.success" }),
        placement: "bottomRight",
      });
    },
  });

  const handleAddCaptain = async (personId: string) => {
    if (entityId && seasonId) {
      await createCaptainRole({
        personId,
        entityId: entityId,
        seasonId,
        role: Roles.Captain,
        fixtureId: fixtureId,
        status: RoleStatuses.Active,
      });
    }
  };

  const addToRoster = async (value) => {
    const personInRoster = data?.filter((person) => person.personId === value.personId) || [];
    if (personInRoster.length > 0) {
      notification.error({
        message: formatMessage({ id: "roster.fixture.error.person.exist" }),
        placement: "bottomRight",
      });
    } else {
      await handleUpsert(value);
    }
  };

  const starterSize = data.filter((item) => item.starter).length;
  const rosterSize = data.length;

  const isAddAllowed = maxRosterSize ? rosterSize < maxRosterSize : true;
  const isAddToStarterAllowed = maxStarterSize ? starterSize < maxStarterSize : true;

  const columns = useGetRosterColumns(
    massEditMode,
    handleUpsert,
    handleDelete,
    deleteCaptainRole,
    handleAddCaptain,
    isAddToStarterAllowed,
    isRosterEditable,
  );

  const { columnsToShow, setSelectedColumns, setSortColumn } = useTableColumns({
    columnSettingsConfig: {
      columnSettingsLocalStorageKey,
      tableConfiguration,
    },
    columnSortConfig: {
      sorterLocalStorageKey: columnSortLocalStorageKey,
    },
    tableColumns: columns,
  });

  const isPending = isLoading || isDeletingCaptain || isCreatingCaptain || isRefetching;

  return (
    <>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        {isRosterEditable && (
          <PermissionCheck
            permissionKeys={fixtureId ? [PermissionNames.FIXTURE_ROSTER] : [PermissionNames.SEASON_ROSTER]}
            permissionOptions={[PermissionOperations.UPDATE]}
          >
            <span>
              <span style={{ marginRight: 5 }}>{formatMessage({ id: "edit.mode" })}</span>
              <Switch disabled={isPending} onChange={onMassEditModeChangedHandler} />
            </span>
          </PermissionCheck>
        )}
        {isRosterEditable && (
          <PermissionCheck
            permissionKeys={fixtureId ? [PermissionNames.FIXTURE_ROSTER] : [PermissionNames.SEASON_ROSTER]}
            permissionOptions={[PermissionOperations.CREATE]}
          >
            <AddPlayerToRosterModal disabled={isPending || !isAddAllowed}>
              {fixtureId ? (
                <AddPlayerToFixtureRoster
                  isAddAllowed={isAddAllowed}
                  isLoading={isLoading}
                  fixtureId={fixtureId}
                  entityId={entityId}
                  handleAdd={addToRoster}
                  tableData={data}
                />
              ) : (
                <AddPlayerToSeasonRoster handleAdd={addToRoster} tableData={data} />
              )}
            </AddPlayerToRosterModal>
          </PermissionCheck>
        )}
        <Button
          disabled={isPending}
          type="primary"
          icon={<i style={{ paddingRight: "5px" }} className="fas fa-images" />}
          onClick={() => navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities/${entityId}/roster/images`)}
        >
          <FormattedMessage id="images.manage" defaultMessage="Manage Images" />
        </Button>
        {!fixtureId && (
          <PermissionCheck
            permissionKeys={[PermissionNames.SEASON_ROSTER]}
            permissionOptions={[PermissionOperations.CREATE, PermissionOperations.UPDATE]}
          >
            <ImportModal
              title={formatMessage({ id: "import.modal.title.seasonRoster" })}
              description={formatMessage({
                id: "import.modal.text.seasonRoster",
              })}
              startImporting={startImporting}
            />
          </PermissionCheck>
        )}
        {!fixtureId && (
          <PermissionCheck permissionKeys={[PermissionNames.SEASON_ROSTER]} permissionOptions={[PermissionOperations.READ]}>
            <ExportModal
              handleExport={startExport}
              defaultFields={defaultSeasonRosterExportFieldNames}
              groupedFields={seasonRosterExportFields}
              localStorageKey={"export.seasonRoster"}
              loading={isExportPending}
            />
          </PermissionCheck>
        )}
        <TableColumnSettings
          setSelectedColumns={setSelectedColumns}
          tableConfiguration={tableConfiguration}
          localStorageKey={columnSettingsLocalStorageKey}
          columns={columns.map((x) => ({ key: x.key, title: x.title }))}
        />
      </div>
      <div style={{ flexBasis: "100%", width: "100%" }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 5 }}>
          <span>{maxStarterSize && `${formatMessage({ id: "roster.starter.size" })} ${starterSize}/${maxStarterSize}`}</span>
          <span>{maxRosterSize && `${formatMessage({ id: "roster.size" })} ${rosterSize}/${maxRosterSize}`}</span>
        </div>
        <Table
          size="small"
          dataSource={data}
          columns={columnsToShow}
          loading={isPending}
          rowKey="personId"
          components={{
            body: { cell: TableCell },
          }}
          rootClassName={styles.tableContainer}
          scroll={{ x: "max-content" }}
          onChange={(_, __, sorter) => setSortColumn(sorter)}
          pagination={{
            current: pagination.currentPage,
            defaultPageSize: pagination.pageSize,
            showSizeChanger: true,
            pageSizeOptions: pagination.pageSizeOptions,
            showQuickJumper: false,
            total: pagination.offset + (data?.length ?? 0) + 1,
            position: ["bottomCenter"],
            onChange: pagination.handlePaginationChange,
            showTotal: () => formatMessage({ id: "pageN" }, { number: pagination.currentPage }),
          }}
        />
      </div>
    </>
  );
};

export default RosterTable;
